import { IconButton, SearchBox, Stack, StackItem } from '@fluentui/react';
import { makeStyles, shorthands } from '@griffel/react';
import { action, autorun, makeAutoObservable, runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { NextPage } from "next";
import Head from "next/head";
import { useCallback, useEffect } from 'react';
import { ISearchOptions } from 'xterm-addon-search';
import 'xterm/css/xterm.css';
import { ResizeObserver } from '../components';
import { GlobalState } from "../state";
import { asyncEffect, createFileStream, formatSize, formatSpeed, Icons, pickFile, ProgressStream, RouteStackProps, saveFile } from '../utils';
import { ChunkStream } from '@yume-chan/stream-extra';
import path from 'path';
import { AdbFeatures, ADB_SYNC_MAX_PACKET_SIZE, LinuxFileType, type AdbSyncEntry } from '@yume-chan/adb';

declare const window: any;

const useClasses = makeStyles({
    count: {
        ...shorthands.padding('0', '0'),
    }
});

let terminal: import('../components/terminal').AdbTerminal | undefined;
if (typeof window !== 'undefined') {
    const { AdbTerminal } = require('../components/terminal');
    terminal = new AdbTerminal();
    window.terminal = terminal;
}

const SEARCH_OPTIONS: ISearchOptions = {
    decorations: {
        matchBackground: '#42557b',
        matchOverviewRuler: '#d18616',
        activeMatchBackground: '#6199ff2f',
        activeMatchColorOverviewRuler: '#d186167e'
    }
};

const state = makeAutoObservable({
    visible: false,
    index: undefined as number | undefined,
    count: undefined as number | undefined,
    setVisible(value: boolean) {
        this.visible = value;
    },

    searchKeyword: '',
    setSearchKeyword(value: string) {
        this.searchKeyword = value;
        terminal!.searchAddon.findNext(value, {
            ...SEARCH_OPTIONS,
            incremental: true,
        });
    },

    searchPrevious() {
        terminal!.searchAddon.findPrevious(this.searchKeyword, SEARCH_OPTIONS);
    },
    searchNext() {
        terminal!.searchAddon.findNext(this.searchKeyword, SEARCH_OPTIONS);
    }
}, {
    searchPrevious: action.bound,
    searchNext: action.bound,
});

if (terminal) {
    terminal.searchAddon.onDidChangeResults((e) => {
        console.log(e);

        runInAction(() => {
            if (e) {
                state.index = e.resultIndex;
                state.count = e.resultCount;
            } else {
                state.index = undefined;
                state.count = undefined;
            }
        });
    });
}

autorun(() => {
    if (!terminal) {
        return;
    }

    if (!GlobalState.device) {
        terminal.socket = undefined;
        return;
    }

    if (!terminal.socket && state.visible) {
        GlobalState.device?.subprocess.shell()
            .then(action(shell => {
                terminal!.socket = shell;
            }), (e) => {
                GlobalState.showErrorDialog(e);
            });
    }
});

const UpIconProps = { iconName: Icons.ChevronUp };
const DownIconProps = { iconName: Icons.ChevronDown };

const Shell: NextPage = (): JSX.Element | null => {
    const classes = useClasses();

    const handleSearchKeywordChange = useCallback((e: unknown, value?: string) => {
        state.setSearchKeyword(value ?? '');
    }, []);

    const handleResize = useCallback(() => {
        terminal!.fit();
    }, []);

    const handleContainerRef = useCallback((container: HTMLDivElement | null) => {
        if (container) {
            terminal!.setContainer(container);
        }
    }, []);

    useEffect(() => {
        state.setVisible(true);
        return () => {
            state.setVisible(false);
        };
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined') {

            window.upload = async (file: File, apath: string) => {
                const sync = await GlobalState.device!.sync();
                try {
                    const itemPath = path.resolve(apath, file.name);
                    // runInAction(() => {
                    //     this.uploading = true;
                    //     this.uploadPath = file.name;
                    //     this.uploadedSize = 0;
                    //     this.uploadTotalSize = file.size;
                    //     this.debouncedUploadedSize = 0;
                    //     this.uploadSpeed = 0;
                    // });

                    // const intervalId = setInterval(action(() => {
                    //     this.uploadSpeed = this.uploadedSize - this.debouncedUploadedSize;
                    //     this.debouncedUploadedSize = this.uploadedSize;
                    // }), 1000);

                    try {
                        await createFileStream(file)
                            .pipeThrough(new ChunkStream(ADB_SYNC_MAX_PACKET_SIZE))
                            .pipeThrough(new ProgressStream(action((uploaded) => {
                                //this.uploadedSize = uploaded;
                            })))
                            .pipeTo(sync.write(
                                itemPath,
                                (LinuxFileType.File << 12) | 0o666,
                                file.lastModified / 1000,
                            ));

                        runInAction(() => {
                            // this.uploadSpeed = this.uploadedSize - this.debouncedUploadedSize;
                            // this.debouncedUploadedSize = this.uploadedSize;
                        });
                    } finally {
                        //clearInterval(intervalId);
                    }
                } catch (e: any) {
                    //GlobalState.showErrorDialog(e);
                    return e;
                } finally {
                    sync.dispose();
                    await GlobalState.device?.subprocess.shell("sync ;")
                        .then(action(shell => {

                        }), (e) => {

                        });
                    // this.loadFiles();
                    // runInAction(() => {
                    //     this.uploading = false;
                    // });
                }
            };


            window.device = GlobalState.device;
            window.terminal = terminal;
        }
    }, [GlobalState.device, terminal]);

    return (
        <Stack {...RouteStackProps}>
            <Head>
                <title>Interactive Shell - Android Web Toolbox</title>
            </Head>
            <StackItem style={{ display: "none" }}>
                <Stack horizontal>
                    <StackItem grow>
                        <SearchBox
                            placeholder="Find"
                            value={state.searchKeyword}
                            onChange={handleSearchKeywordChange}
                            onSearch={state.searchNext}
                        />
                    </StackItem>
                    {state.count === 0 ? (
                        <StackItem className={classes.count} align="center">
                            No results
                        </StackItem>
                    ) : state.count !== undefined ? (
                        <StackItem className={classes.count} align='center'>
                            {state.index! + 1} of {state.count}
                        </StackItem>
                    ) : null}
                    <StackItem>
                        <IconButton
                            disabled={!state.searchKeyword}
                            iconProps={UpIconProps}
                            onClick={state.searchPrevious}
                        />
                    </StackItem>
                    <StackItem>
                        <IconButton
                            disabled={!state.searchKeyword}
                            iconProps={DownIconProps}
                            onClick={state.searchNext}
                        />
                    </StackItem>
                </Stack>
            </StackItem>
            <StackItem grow styles={{ root: { position: 'relative', minHeight: 0, padding: 0, margin: "0 !important" } }}>
                <ResizeObserver onResize={handleResize} />
                <div ref={handleContainerRef} style={{ height: '100%' }} />
            </StackItem>
        </Stack>
    );
};

export default observer(Shell);
